import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { readOrchestratorBaseUrl } from '@root/src/environments/orchestrator-base-url';
import {
  CatalogList,
  ChatSessionDetails,
  ChatSessionPatch,
  ChatSessionSummary,
  ChatSessionsQuery,
  CreateSubscriptionRequest,
  JobsQuery,
  ReportArtifactItem,
  ReportJobItem,
  ReportTemplateItem,
  ReportsQuery,
  CreateReportRequest,
  ApplyConstructorTemplateResponse,
  ComposeReportRequest,
  ConstructorTemplateItem,
  RefreshDatasetRequest,
  RefreshDatasetResponse,
  SaveConstructorTemplateRequest,
  SubscribeConstructorTemplateRequest,
  SubscriptionItem,
  SubscriptionRunItem,
  UpdateSubscriptionRequest
} from './models/analytics-catalog.models';

/**
 * Server-side catalog of the Reporting & Analytics module (chat sessions, report jobs/files,
 * subscriptions). Backed by the orchestrator REST endpoints under `/api/orchestrator`;
 * every call is scoped to the authenticated user by the Bearer token.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsCatalogService {
  private readonly baseUrl = readOrchestratorBaseUrl();

  constructor(private readonly http: HttpClient) {}

  listSessions(query: ChatSessionsQuery = {}): Observable<ChatSessionSummary[]> {
    let params = new HttpParams();
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    if (query.starred != null) {
      params = params.set('starred', String(query.starred));
    }
    if (query.archived) {
      params = params.set('archived', query.archived);
    }
    if (query.take) {
      params = params.set('take', String(query.take));
    }
    return this.http
      .get<CatalogList<ChatSessionSummary>>(`${this.baseUrl}/sessions`, { params })
      .pipe(map((res) => res?.items ?? []));
  }

  getSession(sessionId: string): Observable<ChatSessionDetails> {
    return this.http.get<ChatSessionDetails>(`${this.baseUrl}/sessions/${encodeURIComponent(sessionId)}`);
  }

  updateSession(sessionId: string, patch: ChatSessionPatch): Observable<ChatSessionSummary> {
    return this.http.patch<ChatSessionSummary>(`${this.baseUrl}/sessions/${encodeURIComponent(sessionId)}`, patch);
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/sessions/${encodeURIComponent(sessionId)}`);
  }

  listTemplates(): Observable<ReportTemplateItem[]> {
    return this.http
      .get<CatalogList<ReportTemplateItem>>(`${this.baseUrl}/templates`)
      .pipe(map((res) => res?.items ?? []));
  }

  createReport(body: CreateReportRequest): Observable<ReportJobItem> {
    return this.http.post<ReportJobItem>(`${this.baseUrl}/reports`, body);
  }

  composeReport(body: ComposeReportRequest): Observable<ReportJobItem> {
    return this.http.post<ReportJobItem>(`${this.baseUrl}/reports/compose`, body);
  }

  listConstructorTemplates(): Observable<ConstructorTemplateItem[]> {
    return this.http
      .get<CatalogList<ConstructorTemplateItem>>(`${this.baseUrl}/reports/constructor-templates`)
      .pipe(map((res) => res?.items ?? []));
  }

  saveConstructorTemplate(body: SaveConstructorTemplateRequest): Observable<ConstructorTemplateItem> {
    return this.http.post<ConstructorTemplateItem>(`${this.baseUrl}/reports/constructor-templates`, body);
  }

  subscribeConstructorTemplate(
    templateId: string,
    body: SubscribeConstructorTemplateRequest
  ): Observable<SubscriptionItem> {
    return this.http.post<SubscriptionItem>(
      `${this.baseUrl}/reports/constructor-templates/${encodeURIComponent(templateId)}/subscribe`,
      body
    );
  }

  applyConstructorTemplate(templateId: string, sessionId: string | null): Observable<ApplyConstructorTemplateResponse> {
    return this.http.post<ApplyConstructorTemplateResponse>(
      `${this.baseUrl}/reports/constructor-templates/${encodeURIComponent(templateId)}/apply`,
      { session_id: sessionId }
    );
  }

  refreshDataset(datasetId: string, body: RefreshDatasetRequest): Observable<RefreshDatasetResponse> {
    return this.http.post<RefreshDatasetResponse>(
      `${this.baseUrl}/reports/datasets/${encodeURIComponent(datasetId)}/refresh`,
      body
    );
  }

  listReports(query: ReportsQuery = {}): Observable<ReportArtifactItem[]> {
    return this.http
      .get<CatalogList<ReportArtifactItem>>(`${this.baseUrl}/reports`, { params: this.toParams(query) })
      .pipe(map((res) => res?.items ?? []));
  }

  listJobs(query: JobsQuery = {}): Observable<ReportJobItem[]> {
    return this.http
      .get<CatalogList<ReportJobItem>>(`${this.baseUrl}/jobs`, { params: this.toParams(query) })
      .pipe(map((res) => res?.items ?? []));
  }

  getJob(jobId: string): Observable<ReportJobItem> {
    return this.http.get<ReportJobItem>(`${this.baseUrl}/jobs/${encodeURIComponent(jobId)}`);
  }

  listSubscriptions(active?: boolean): Observable<SubscriptionItem[]> {
    let params = new HttpParams();
    if (active != null) {
      params = params.set('active', String(active));
    }
    return this.http
      .get<CatalogList<SubscriptionItem>>(`${this.baseUrl}/subscriptions`, { params })
      .pipe(map((res) => res?.items ?? []));
  }

  createSubscription(body: CreateSubscriptionRequest): Observable<SubscriptionItem> {
    return this.http.post<SubscriptionItem>(`${this.baseUrl}/subscriptions`, body);
  }

  updateSubscription(subscriptionId: string, body: UpdateSubscriptionRequest): Observable<SubscriptionItem> {
    return this.http.patch<SubscriptionItem>(
      `${this.baseUrl}/subscriptions/${encodeURIComponent(subscriptionId)}`,
      body
    );
  }

  cancelSubscription(subscriptionId: string): Observable<SubscriptionItem> {
    return this.http.delete<SubscriptionItem>(`${this.baseUrl}/subscriptions/${encodeURIComponent(subscriptionId)}`);
  }

  listRuns(subscriptionId: string, take = 20): Observable<SubscriptionRunItem[]> {
    const params = new HttpParams().set('take', String(take));
    return this.http
      .get<CatalogList<SubscriptionRunItem>>(
        `${this.baseUrl}/subscriptions/${encodeURIComponent(subscriptionId)}/runs`,
        { params }
      )
      .pipe(map((res) => res?.items ?? []));
  }

  private toParams(query: ReportsQuery | JobsQuery): HttpParams {
    let params = new HttpParams();
    Object.entries(query as Record<string, string | number | undefined>).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }
}
